/**
 * ActionWeekTab — Phase 3 dashboard.
 *
 * One-glance view of everything that needs the broker's attention in
 * the next 7-90 days. Three sections:
 *   1. Fixed-rate expiries (90/60/30 day buckets)
 *   2. Clawback windows ending soon
 *   3. Care tasks due this week (incl. overdue)
 *
 * "Refresh" runs the backend trigger engine (which seeds new milestone
 * tasks for any loans with fixed expiries inside the 90-day window).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Flame, AlertTriangle, ListChecks, RefreshCw, Phone, Mail, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const fmtMoney = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
};

const BUCKET_STYLES = {
  red:   'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
};

const ActionWeekTab = ({ onOpenLoan }) => {
  const { token, user } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scope, setScope] = useState('team');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ mine_only: scope === 'mine' ? 'true' : 'false' });
      const r = await fetch(`${backendUrl}/api/settled-loans/action-week?${params}`, { headers: authHeaders });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, [backendUrl, authHeaders, scope]);

  useEffect(() => { load(); }, [load]);

  const runTriggerEngine = async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/trigger-engine/run`, { method: 'POST', headers: authHeaders });
      if (!r.ok) throw new Error('Failed');
      const j = await r.json();
      toast.success(`Sweep complete · ${j.fixed_expiry_seeded} new milestones`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setRefreshing(false); }
  };

  if (loading || !data) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="action-week-tab">
      {/* Controls */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {[{ k: 'team', label: 'All' }, { k: 'mine', label: 'Mine' }].map(({ k, label }) => (
            <button
              key={k}
              type="button"
              onClick={() => setScope(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                scope === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-blue-300'
              }`}
              data-testid={`actionweek-scope-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={runTriggerEngine}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-50"
          data-testid="actionweek-sweep-btn"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Sweep now
        </button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Fixed expiries (90d)"  value={data.totals.fixed_expiries}     accent={data.totals.fixed_red ? 'red' : 'amber'} icon={Flame} />
        <Stat label="Clawback alerts"       value={data.totals.clawback_alerts}    accent={data.totals.clawback_alerts ? 'red' : 'slate'} icon={AlertTriangle} />
        <Stat label="Overdue care"          value={data.totals.care_overdue}       accent={data.totals.care_overdue ? 'red' : 'slate'} icon={ListChecks} />
        <Stat label="Due this week"         value={data.totals.care_due_this_week} accent="blue" icon={ListChecks} />
      </div>

      {/* Fixed expiries */}
      <Section title="Fixed-rate expiries" subtitle="Pricing review window — refinance opportunity or re-fix conversation" icon={Flame}>
        {data.fixed_expiries.length === 0 ? (
          <Empty>No fixed expiries in the next 90 days.</Empty>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.fixed_expiries.map((f) => (
              <li key={f.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors" data-testid="fixed-expiry-row">
                <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${BUCKET_STYLES[f.bucket]}`}>
                  <Flame className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <button type="button" onClick={() => onOpenLoan?.(f.id)} className="font-semibold text-slate-900 dark:text-white hover:text-blue-600 text-sm truncate">
                      {f.client_name}
                    </button>
                    <span className="font-mono text-[10px] text-slate-400">{f.loan_number}</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide ${BUCKET_STYLES[f.bucket]}`}>
                      {f.days_until_expiry}d
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {f.lender} · {fmtMoney(f.loan_amount)} · {Number(f.interest_rate).toFixed(2)}% {f.rate_type} · expires {fmtDate(f.fixed_rate_expiry)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Clawback alerts */}
      <Section title="Clawback radar" subtitle="Commission still recallable — protect the trail before refinance temptation" icon={AlertTriangle}>
        {data.clawback_alerts.length === 0 ? (
          <Empty>No loans inside the clawback window.</Empty>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.clawback_alerts.map((c) => (
              <li key={c.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors" data-testid="clawback-row">
                <div className="h-9 w-9 rounded-xl bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300 grid place-items-center shrink-0">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <button type="button" onClick={() => onOpenLoan?.(c.id)} className="font-semibold text-slate-900 dark:text-white hover:text-blue-600 text-sm truncate">
                      {c.client_name}
                    </button>
                    <span className="font-mono text-[10px] text-slate-400">{c.loan_number}</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300">
                      {c.days_until_clawback_end}d to clear
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {c.lender} · {fmtMoney(c.loan_amount)} · clawback ends {fmtDate(c.clawback_end_date)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Care this week */}
      <Section title="Care tasks this week" subtitle="Calls, check-ins and reviews on the calendar in the next 7 days" icon={ListChecks}>
        {data.care_this_week.length === 0 ? (
          <Empty>Nothing on the calendar this week.</Empty>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.care_this_week.map((t) => {
              const I = t.type === 'call' ? Phone : t.type === 'email' ? Mail : t.type === 'review' ? FileText : ListChecks;
              return (
                <li key={t.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors" data-testid="care-week-row">
                  <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${t.overdue ? BUCKET_STYLES.red : 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'}`}>
                    <I className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <button type="button" onClick={() => onOpenLoan?.(t.loan_id)} className="font-semibold text-slate-900 dark:text-white hover:text-blue-600 text-sm truncate">
                        {t.client_name}
                      </button>
                      <span className="font-mono text-[10px] text-slate-400">{t.loan_number}</span>
                      {t.overdue && <span className="px-1.5 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300">Overdue</span>}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{t.title} · {fmtDate(t.scheduled_at)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* Hint about scope */}
      {scope === 'team' && user?.role === 'broker' && (
        <p className="text-[11px] text-slate-500 italic px-1">Showing the whole loan book. Switch to &quot;Mine&quot; to see only loans assigned to you.</p>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, accent }) => {
  const accents = {
    red:   'text-red-700 dark:text-red-300',
    amber: 'text-amber-700 dark:text-amber-300',
    blue:  'text-blue-700 dark:text-blue-300',
    slate: 'text-slate-700 dark:text-slate-200',
  };
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-3 flex items-center gap-3">
      <div className={`h-9 w-9 rounded-xl bg-slate-100 dark:bg-slate-800 grid place-items-center ${accents[accent]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
        <div className={`text-lg font-bold leading-tight ${accents[accent]}`}>{value}</div>
      </div>
    </div>
  );
};

const Section = ({ title, subtitle, icon: Icon, children }) => (
  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
    <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-start gap-3">
      <div className="h-7 w-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 grid place-items-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
        <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
      </div>
    </div>
    {children}
  </div>
);

const Empty = ({ children }) => (
  <div className="py-10 text-center text-xs text-slate-500">{children}</div>
);

export default ActionWeekTab;
